import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Building, 
  Clock, 
  DollarSign,
  Users,
  Bell,
  Shield,
  Database,
  Mail,
  Phone,
  MapPin,
  Save,
  RefreshCw,
  Download,
  Upload,
  Globe,
  Palette,
  Calendar,
  Plus
} from 'lucide-react';
import { Card, Button, Input, Textarea, Select, Badge } from '../../components/ui';
import AdminSidebar from '../../components/layout/AdminSidebar';
import { servicesAPI } from '../../services/api';

const AdminSettings: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [clinicInfo, setClinicInfo] = useState({
    name: 'ClinicIT Medical Center',
    tagline: 'Your Health, Our Priority',
    email: 'info@clinicit.com',
    phone: '+1 (555) 123-4567',
    address: {
      street: '123 Medical Plaza',
      city: 'Healthcare City',
      state: 'CA',
      zipCode: '90210'
    },
    website: 'https://clinicit.com',
    description: 'A modern medical clinic providing comprehensive healthcare services.',
    businessHours: {
      monday: { open: '08:00', close: '18:00', isOpen: true },
      tuesday: { open: '08:00', close: '18:00', isOpen: true },
      wednesday: { open: '08:00', close: '18:00', isOpen: true },
      thursday: { open: '08:00', close: '18:00', isOpen: true },
      friday: { open: '08:00', close: '17:00', isOpen: true },
      saturday: { open: '09:00', close: '15:00', isOpen: true },
      sunday: { open: '00:00', close: '00:00', isOpen: false }
    }
  });
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load services from API
  const loadServices = async () => {
    try {
      const result = await servicesAPI.getAll();
      if (result.success && result.data) {
        setServices(result.data);
      }
    } catch (err) {
      console.error('Error loading services:', err);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const tabs = [
    { id: 'general', name: 'General', icon: Building },
    { id: 'hours', name: 'Business Hours', icon: Clock },
    { id: 'services', name: 'Services & Pricing', icon: DollarSign },
    { id: 'staff', name: 'Staff Management', icon: Users },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'backup', name: 'Backup & Data', icon: Database }
  ];

  const handleSave = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1000);
  };

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return { value: `${hour}:00`, label: `${hour}:00` };
  });

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Clinic Information</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Clinic Name"
              value={clinicInfo.name}
              onChange={(e) => setClinicInfo(prev => ({ ...prev, name: e.target.value }))}
            />
            <Input
              label="Tagline"
              value={clinicInfo.tagline}
              onChange={(e) => setClinicInfo(prev => ({ ...prev, tagline: e.target.value }))}
            />
          </div>
          
          <Textarea
            label="Description"
            value={clinicInfo.description}
            onChange={(e) => setClinicInfo(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
          />
        </div>
      </Card>

      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Phone Number"
              value={clinicInfo.phone}
              onChange={(e) => setClinicInfo(prev => ({ ...prev, phone: e.target.value }))}
              icon={<Phone className="w-4 h-4" />}
            />
            <Input
              label="Email Address"
              type="email"
              value={clinicInfo.email}
              onChange={(e) => setClinicInfo(prev => ({ ...prev, email: e.target.value }))}
              icon={<Mail className="w-4 h-4" />}
            />
          </div>
          
          <Input
            label="Website"
            value={clinicInfo.website}
            onChange={(e) => setClinicInfo(prev => ({ ...prev, website: e.target.value }))}
            icon={<Globe className="w-4 h-4" />}
          />
        </div>
      </Card>

      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Address</h3>
        </div>
        <div className="p-6 space-y-4">
          <Input
            label="Street Address"
            value={clinicInfo.address.street}
            onChange={(e) => setClinicInfo(prev => ({
              ...prev,
              address: { ...prev.address, street: e.target.value }
            }))}
            icon={<MapPin className="w-4 h-4" />}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="City"
              value={clinicInfo.address.city}
              onChange={(e) => setClinicInfo(prev => ({
                ...prev,
                address: { ...prev.address, city: e.target.value }
              }))}
            />
            <Input
              label="State"
              value={clinicInfo.address.state}
              onChange={(e) => setClinicInfo(prev => ({
                ...prev,
                address: { ...prev.address, state: e.target.value }
              }))}
            />
            <Input
              label="ZIP Code"
              value={clinicInfo.address.zipCode}
              onChange={(e) => setClinicInfo(prev => ({
                ...prev,
                address: { ...prev.address, zipCode: e.target.value }
              }))}
            />
          </div>
        </div>
      </Card>
    </div>
  );

  const renderHoursSettings = () => (
    <Card>
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Business Hours</h3>
        <p className="text-sm text-gray-600">Set your clinic's operating hours for each day</p>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {Object.entries(clinicInfo.businessHours).map(([day, hours]) => (
            <div key={day} className="flex items-center space-x-4">
              <div className="w-24">
                <span className="font-medium text-gray-900 capitalize">{day}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={hours.isOpen}
                  onChange={(e) => setClinicInfo(prev => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      [day]: { ...hours, isOpen: e.target.checked }
                    }
                  }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">Open</span>
              </div>
              
              {hours.isOpen && (
                <>
                  <Select
                    options={timeOptions}
                    value={hours.open}
                    onChange={(value) => setClinicInfo(prev => ({
                      ...prev,
                      hours: {
                        ...prev.businessHours,
                        [day]: { ...hours, open: value }
                      }
                    }))}
                    className="w-32"
                  />
                  <span className="text-gray-500">to</span>
                  <Select
                    options={timeOptions}
                    value={hours.close}
                    onChange={(value) => setClinicInfo(prev => ({
                      ...prev,
                      hours: {
                        ...prev.businessHours,
                        [day]: { ...hours, close: value }
                      }
                    }))}
                    className="w-32"
                  />
                </>
              )}
              
              {!hours.isOpen && (
                <span className="text-gray-500 italic">Closed</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );

  const renderServicesSettings = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Service Categories</h3>
              <p className="text-sm text-gray-600">Manage your medical service offerings</p>
            </div>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {['General Medicine', 'Cardiology', 'Orthopedics', 'Dermatology'].map((category) => (
              <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-900">{category}</span>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm">Edit</Button>
                  <Button variant="ghost" size="sm">Delete</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Payment Settings</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Default Consultation Fee"
              type="number"
              placeholder="150"
              icon={<DollarSign className="w-4 h-4" />}
            />
            <Select
              label="Currency"
              options={[
                { value: 'USD', label: 'US Dollar ($)' },
                { value: 'EUR', label: 'Euro (€)' },
                { value: 'GBP', label: 'British Pound (£)' }
              ]}
              value="USD"
              onChange={() => {}}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="text-sm text-gray-700">Accept online payments</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="text-sm text-gray-700">Require payment confirmation</span>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Email Notifications</h3>
        </div>
        <div className="p-6 space-y-4">
          {[
            'New appointment bookings',
            'Appointment cancellations',
            'Patient registration',
            'Payment confirmations',
            'System updates'
          ].map((notification) => (
            <div key={notification} className="flex items-center justify-between">
              <span className="text-gray-700">{notification}</span>
              <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">SMS Notifications</h3>
        </div>
        <div className="p-6 space-y-4">
          {[
            'Appointment reminders',
            'Appointment confirmations',
            'Emergency alerts'
          ].map((notification) => (
            <div key={notification} className="flex items-center justify-between">
              <span className="text-gray-700">{notification}</span>
              <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderBackupSettings = () => (
    <div className="space-y-6">
      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Data Backup</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div>
              <p className="font-medium text-green-900">Last Backup</p>
              <p className="text-sm text-green-700">Today at 3:00 AM</p>
            </div>
            <Badge variant="success">Completed</Badge>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button>
              <Download className="w-4 h-4 mr-2" />
              Download Backup
            </Button>
            <Button variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Create Backup Now
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Data Import/Export</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Export Data</h4>
              <p className="text-sm text-gray-600 mb-4">Download your clinic data in CSV format</p>
              <Button variant="outline" className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Export All Data
              </Button>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Import Data</h4>
              <p className="text-sm text-gray-600 mb-4">Upload data from CSV files</p>
              <Button variant="outline" className="w-full">
                <Upload className="w-4 h-4 mr-2" />
                Import Data
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'hours':
        return renderHoursSettings();
      case 'services':
        return renderServicesSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'backup':
        return renderBackupSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminSidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
                <p className="text-gray-600">Manage your clinic configuration</p>
              </div>
              
              <div className="flex items-center space-x-4">
                <Button 
                  onClick={handleSave}
                  disabled={loading}
                  className={saved ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : saved ? (
                    <span>✓ Saved</span>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex">
          {/* Sidebar Navigation */}
          <div className="w-64 bg-white border-r border-gray-200 min-h-screen">
            <div className="p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;